import Grid from "@mui/material/Grid";
import { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import ConfirmButton from "../fundamentals/ConfirmButton.jsx";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { addTimeOffRequestWOPFreezeEvent, fetchLatestTimeOffRequestWOPFreezeEvent } from "../../api/timeOffRequest.js";
import { useSelector } from "react-redux";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { buildWOPFreezeEventPayload } from "../../util/timeOffRequestEventBuilder.js";
import { getIsoDateFormat } from "../../util/dateTimeFormatter.js";
import { DateTime } from "luxon";
import { API_POLLING_INTERVAL, API_TIMEOUT } from "../../util/constants.js";

const useStyles = makeStyles((theme) => ({
  freezeButton: {
    width: "140px",
    float: "right",
    fontWeight: "bold",
  },
}));

const TimeOffRequestWOPFreeze = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isActionCheckDialogOpen, setIsActionCheckDialogOpen] = useState(false);
  const [isSubmitConfirmationDialogOpen, setIsSubmitConfirmationDialogOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isActionError, setIsActionError] = useState(false);
  const [actionErrors, setActionErrors] = useState("");
  const [isActionSuccess, setIsActionSuccess] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");

  const handleClickDialogOpen = () => {
    setIsActionCheckDialogOpen(true);
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsActionCheckDialogOpen(false);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    props.onFreezeRefresh();
    setIsActionError(false);
    setActionErrors("");
    setIsActionSuccess(false);
    setActionSuccess("");
    setIsSubmitConfirmationDialogOpen(false);
  };

  const handleSubmit = async () => {
    // Call the API to add the WOP freeze event
    try {
      setIsActionLoading(true);
      setIsActionError(false);
      setActionErrors("");
      setIsActionSuccess(false);
      setActionSuccess("");
      const employeeData = {
        eid: userSession.eid,
        firstName: userSession.firstName,
        lastName: userSession.lastName,
      };
      const payload = buildWOPFreezeEventPayload(
        props.selectedDate,
        !props.isFrozen,
        props.workgroup,
        props.wopType,
        props.station,
        employeeData
      );
      const response = await addTimeOffRequestWOPFreezeEvent(payload);
      const result = await response.json();
      if (response.status === 200 && result) {
        const requestId = result.requestId;
        longPollingLatestWOPFreezeEvent(requestId);
      } else {
        setIsActionError(true);
        setActionErrors("Something went wrong. Please try again!");
        setIsActionLoading(false);
        setIsActionCheckDialogOpen(false);
        setIsSubmitConfirmationDialogOpen(true);
      }
    } catch (err) {
      console.error("Error in onPublishConfirm: ", err);
      setIsActionError(true);
      setActionErrors("Something went wrong. Please try again!");
      setIsActionLoading(false);
    }
  };

  const longPollingLatestWOPFreezeEvent = (requestId) => {
    const startTime = DateTime.now();
    function fetchFreezeEvent() {
      fetchLatestTimeOffRequestWOPFreezeEvent(
        props.station,
        props.workgroup,
        getIsoDateFormat(props.selectedDate),
        props.wopType
      )
        .then((response) => response.json())
        .then((result) => {
          if (result?.latestWopFreezeEvent?.requestId === requestId) {
            setIsActionSuccess(true);
            setActionSuccess("Your request was submitted successfully.");
            setIsActionLoading(false);
            setIsActionCheckDialogOpen(false);
            setIsSubmitConfirmationDialogOpen(true);
          } else if (!result || result.error) {
            // stop polling if one call fails
            setIsActionError(true);
            setActionErrors("Something went wrong. Please try again!");
            setIsActionLoading(false);
            setIsActionCheckDialogOpen(false);
            setIsSubmitConfirmationDialogOpen(true);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchFreezeEvent, API_POLLING_INTERVAL);
            } else {
              // stop polling when timeout
              setIsActionError(true);
              setActionErrors("Your request timed out. Please try again!");
              setIsActionLoading(false);
              setIsActionCheckDialogOpen(false);
              setIsSubmitConfirmationDialogOpen(true);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching latest WOP freeze event:", error);
          setIsActionError(true);
          setActionErrors("Something went wrong. Please try again!");
          setIsActionLoading(false);
        });
    }

    fetchFreezeEvent();
  };

  return (
    <div data-testid="tor-admin-freeze-actions">
      <Button
        className={classes.freezeButton}
        variant="contained"
        color="primary"
        onClick={handleClickDialogOpen}
        disabled={props.freezeButtonDisabled}
      >
        {props.isFrozen ? "Stop Award" : "Start Award"}
      </Button>

      {isActionCheckDialogOpen && (
        <Dialog
          className="action-check-dialog"
          data-testid="action-check-dialog"
          fullWidth
          maxWidth="md"
          open={isActionCheckDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
            Confirm
          </DialogTitle>
          <DialogContent className="action-check-dialog-content">
            <Grid container>
              <Grid item xs={12}>
                <DialogContentText component={"div"} id="alert-dialog-slide-description">
                  {props.isFrozen
                    ? "Are you sure you want to stop the award?"
                    : "Are you sure you want to start the award? This will freeze the list for all admins. New requests will not appear until the award is stopped."}
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className="action-check-dialog-buttons">
            <ConfirmButton
              variant="outlined"
              color="primary"
              onClick={handleClickDialogClose}
              isLoading={isActionLoading}
            >
              No
            </ConfirmButton>
            <ConfirmButton variant="contained" color="primary" onClick={handleSubmit} isLoading={isActionLoading}>
              Yes
            </ConfirmButton>
          </DialogActions>
        </Dialog>
      )}
      {isSubmitConfirmationDialogOpen && (
        <Dialog
          className="action-check-dialog"
          data-testid="action-check-dialog"
          fullWidth
          maxWidth="md"
          open={isSubmitConfirmationDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickSubmitDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
            Confirm
          </DialogTitle>
          <DialogContent className="action-check-dialog-content">
            <Grid container>
              <Grid item xs={12}>
                <DialogContentText component={"div"} id="alert-dialog-slide-description">
                  {isActionError ? <Alert severity="error">{actionErrors}</Alert> : ""}
                  {isActionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : ""}
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className="action-check-dialog-buttons">
            <ConfirmButton
              variant="contained"
              color="primary"
              onClick={handleClickSubmitDialogClose}
              isLoading={isActionLoading}
            >
              OK
            </ConfirmButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

TimeOffRequestWOPFreeze.propTypes = {
  freezeButtonDisabled: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  workgroup: PropTypes.string.isRequired,
  wopType: PropTypes.string.isRequired,
  selectedDate: PropTypes.object,
  isFrozen: PropTypes.bool.isRequired,
  onFreezeRefresh: PropTypes.func.isRequired,
};

export default TimeOffRequestWOPFreeze;
